import React, { useEffect, useState } from "react";
import axiosInstance from "../lib/axiosInstance";

function Notifications() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const limit = 10;

  const load = async (targetPage) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/brand/notifications", {
        params: { page: targetPage, limit },
      });
      const payload = res.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      const total = payload.total || data.length;
      setItems(data);
      setPage(targetPage);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (err) {
      console.error("Failed to load notifications", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (notification) => {
    try {
      setDetailLoading(true);
      const res = await axiosInstance.get(`/brand/notifications/${notification.notification_id}`);
      const payload = res.data || {};
      setSelected(payload.data || null);
      // Refresh list so read status updates
      load(page);
    } catch (err) {
      console.error("Failed to load notification detail", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelected(null);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <div className="max-w-4xl mx-auto py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="lux-heading text-2xl m-0">Notifications</h1>
        </div>
        <div className="card">
          <div className="p-4 border-b border-[var(--border)] flex items-center justify-between">
            <div className="text-sm text-[var(--muted)]">
              Brand-wide updates, offers, and participation messages from admin.
            </div>
            <div className="text-xs text-[var(--muted)]">
              Page {page} of {totalPages}
            </div>
          </div>
          {loading && items.length === 0 ? (
            <div className="p-6 text-sm text-[var(--muted)]">Loading notifications…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-sm text-[var(--muted)]">
              No notifications yet. You’ll see offers and participation updates here.
            </div>
          ) : (
            <div className="divide-y divide-[var(--border)]">
              {items.map((n) => {
                const isRead = !!n.is_read;
                return (
                  <button
                    key={n.brand_notification_id}
                    onClick={() => openDetail(n)}
                    className="w-full flex items-start justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--surface-soft)]"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`mt-1 w-2 h-2 rounded-full ${isRead ? "bg-[var(--border)]" : "bg-emerald-500"
                          }`}
                      />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs inline-flex items-center rounded-full px-2 py-0.5 bg-gray-100 text-gray-700 capitalize">
                            {n.type || "general"}
                          </span>
                          <span className="text-xs text-[var(--muted)]">
                            {n.created_at
                              ? new Date(n.created_at).toLocaleString()
                              : ""}
                          </span>
                        </div>
                        <div className="font-medium text-[var(--text)]">
                          {n.title}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-[var(--muted)]">
                      {isRead ? "Read" : "Unread"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border)] text-xs text-[var(--muted)]">
              <div>
                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => load(Math.max(1, page - 1))}
                  disabled={page === 1 || loading}
                  className="px-3 py-1 rounded border border-[var(--border)] bg-[var(--surface)] disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => load(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || loading}
                  className="px-3 py-1 rounded border border-[var(--border)] bg-[var(--surface)] disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {selected && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs inline-flex items-center rounded-full px-2 py-0.5 bg-gray-100 text-gray-700 capitalize">
                      {selected.type || "general"}
                    </span>
                    <span className="text-xs text-[var(--muted)]">
                      {selected.created_at
                        ? new Date(selected.created_at).toLocaleString()
                        : ""}
                    </span>
                  </div>
                  <h2 className="text-base font-semibold text-[var(--text)] m-0">
                    {selected.title}
                  </h2>
                </div>
                <button
                  onClick={closeDetail}
                  className="px-3 py-1 rounded border border-[var(--border)] bg-[var(--surface-soft)] text-sm"
                >
                  Close
                </button>
              </div>
              <div className="px-4 py-4 space-y-4">
                {selected.image_url && (() => {
                  const raw = selected.image_url;
                  const imageSrc = raw.startsWith("http") ? raw : `${(axiosInstance.defaults.baseURL || "").replace(/\/api\/?$/, "")}${raw.startsWith("/") ? raw : `/${raw}`}`;
                  return (
                    <div>
                      <img
                        src={imageSrc}
                        alt="Notification"
                        className="w-full max-h-56 object-cover rounded-lg border border-[var(--border)]"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </div>
                  );
                })()}
                <div
                  className="prose prose-sm max-w-none text-[var(--text)]"
                  dangerouslySetInnerHTML={{ __html: selected.content_html || "" }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;

